import { z } from "zod/v4";

// ---------------------------------------------------------------------------
// Surface vocabulary — lives here (not functions/agentic-logic/types) because
// it is a field of AgenticLogicPlacementDto (./placements) as well as the
// serving envelope's own request/telemetry shapes.
// ---------------------------------------------------------------------------

/**
 * Surfaces a recommendation can render on. Wire format — append-only.
 * Deliberately has NO "headless" value: API callers declare the surface they
 * render.
 */
export const AgenticLogicSurfaceList = [
  "onsite",
  "checkout",
  "post-purchase",
] as const;
export const AgenticLogicSurfaceSchema = z.enum(AgenticLogicSurfaceList);
export type AgenticLogicSurface = (typeof AgenticLogicSurfaceList)[number];

/**
 * Merchant-facing names for the serving surfaces.
 *
 * Owned here rather than in the admin bundle because the sentence a merchant
 * reads about a rule's evaluation budget is now COMPOSED in this package (see
 * `renderAgenticLogicBudgetBasis`) and rendered on both sides of the wire. Two
 * label maps would be free to drift into calling `post-purchase` two different
 * things on two screens.
 *
 * `satisfies` rather than a bare annotation so adding a surface to
 * {@link AgenticLogicSurfaceList} without a label is a compile error here
 * rather than an `undefined` in merchant-facing copy.
 */
export const AGENTIC_LOGIC_SURFACE_LABEL = {
  onsite: "Onsite",
  checkout: "Checkout",
  "post-purchase": "Post-purchase",
} as const satisfies Record<AgenticLogicSurface, string>;

/**
 * A non-empty, duplicate-free set of surfaces — the shape every "which
 * surfaces" field uses, so the rule DTO, the create contract and the assignment
 * guard cannot validate it three slightly different ways.
 *
 * The refinement sits on the ARRAY rather than on an enclosing object on
 * purpose: `CreateAgenticLogicRuleRequestBaseSchema` is `.extend()`ed once per
 * `managedBy` variant, and under Zod v4 `.extend()` / `.omit()` do not survive a
 * refinement applied at the object level.
 *
 * Empty is refused rather than treated as "anywhere": a rule that can serve
 * nowhere is a configuration mistake, and reading `[]` as unrestricted is what
 * turns a declaration into an opt-out.
 */
export const AgenticLogicSurfaceSetSchema = z
  .array(AgenticLogicSurfaceSchema)
  .min(1)
  .refine(
    (surfaces) => new Set(surfaces).size === surfaces.length,
    "surfaces must not repeat"
  );
export type AgenticLogicSurfaceSet = z.infer<
  typeof AgenticLogicSurfaceSetSchema
>;
